import * as React from "react";
import { useEffect, useRef } from "react";

import ReactDOM from "react-dom";
import { Link, Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import { useTheme } from "@emotion/react";
import Toolbar from "@mui/material/Toolbar";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import { styled } from "@mui/material/styles";
import ListItem from "@mui/material/ListItem";
import MenuIcon from "@mui/icons-material/Menu";
import IconButton from "@mui/material/IconButton";
import CssBaseline from "@mui/material/CssBaseline";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Backdrop, useMediaQuery } from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";

import { Loader } from "../common/loader";
import CallUsNow from "../common/CallUsNow";
import LogoIcon from "../../assets/icons/ep-icon-blue.png";
import PromocodeIcon from "../../assets/icons/sell.svg";
import SendInviteIcon from "../../assets/icons/send.svg";
import ProvidersIcon from "../../assets/icons/group.svg";
import ExportIcon from "../../assets/icons/ios_share.svg";
import LogsIcon from "../../assets/icons/web_stories.svg";
import AdminAccountMenu from "../layout/adminAccountMenu";
import AddNewUserIcon from "../../assets/icons/add_user.svg";
import MenuIconImg from "../../assets/dashboard/menu_open.svg";
import SidebarLogo from "../../assets/footer/equity black.png";
import CookieConsentBanner from "../common/CookieConsentBanner";
import CountyIcon from "../../assets/icons/account_balance.svg";
import RecordingFeesIcon from "../../assets/icons/local_atm.svg";
import PropertiesIcon from "../../assets/icons/holiday_village.svg";
import TitleCompanyIcon from "../../assets/icons/title_company.svg";
import PropertiesMonitoringIcon from "../../assets/icons/radar.svg";
import ProfessionalsIcon from "../../assets/icons/professionals.svg";
import RequestDocumentsIcon from "../../assets/icons/difference.svg";
import PropertiesIconSVG from "../common/customSVGs/PropertiesIconSVG";
import { userSigupAction } from "../../redux/slices/user/userSignUpSlice";
import ServiceProvidersIcon from "../../assets/icons/service_provider.svg";
import { userProfileAction } from "../../redux/slices/user/userProfileSlice";
import AdminUnlockPinSetupModal from "./AdminDashboard/AdminUnlockPinSetupModal";
import CountyRecorderMenu from "./CountyRecorderMenu";
import {
  carPartnerFromProfile,
  isCountyRecorderRole,
  rememberAdminPartnerScope,
} from "./countyRecorderAccess";
import { danEmail, ROLES, thyEpEmail } from "../helpers/constants";
import { propertiesAction } from "../../redux/slices/properties/propertiesSlice";
import { exportCSVDetialsAction } from "../../redux/slices/export-csv/exportcsvSlice";
import {
  decryptPayload,
  findAllowedPrefixData,
  getObjLength,
  redirectToAppPath,
} from "../helpers/utils";
import { propertyUsersAction } from "../../redux/slices/admin/propertyUsers/propertyUsersSlice";
import useAdminPermissions from "../helpers/useAdminPermissions";
import "./AdminPageLayout/adminLayout.scss";
import "../../styles/appbar.scss";
import "../../styles/sidebar.scss";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  boxShadow: "0px 0px 2px rgba(52, 75, 91, 0.3)",
  border: "none",
  zIndex: "999",
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  boxShadow: "0px 0px 2px rgba(52, 75, 91, 0.3)",
  border: "none",
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("md")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  [theme.breakpoints.down("md")]: {
    width: "0",
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: theme.spacing(0, 3),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  marginLeft: theme.spacing(7),
  width: `calc(100% - ${theme.spacing(8)} - 1.5px)`,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    marginLeft: "0",
  },
  backgroundColor: "#FFFFFF",
  boxShadow: "0px 0px 2px rgba(52, 75, 91, 0.3)",
  // zIndex: theme.zIndex.drawer + 1,
  zIndex: "9",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    [theme.breakpoints.down("md")]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
    },
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function AdminSideBar({ comp: legacyComp }) {
  const pageContent = legacyComp ?? <Outlet />;
  const dispatch = useDispatch();
  const theme = useTheme();
  const isTab = useMediaQuery(theme.breakpoints.down(992));
  const [open, setOpen] = React.useState(true);
  const [openAlert, setOpenAlert] = React.useState(true);
  const [showAdminPinSetup, setShowAdminPinSetup] = React.useState(false);
  const lastProfileTokenRef = useRef(null);
  const profileRequestTokenRef = useRef(null);

  const { userProfileDetails, profileLoader } = useSelector(
    (state) => state.userProfile,
  );
  const clearUserData = () => {
    dispatch(userProfileAction.clearUserProfileData());
  };

  const handleModalClosing = () => {
    dispatch(exportCSVDetialsAction.getExportCSVFile({})); // cleared the common export csv redux values
    if (window.innerWidth < 575) {
      setOpen((prevState) => !prevState);
    }
    const pathname = window.location.pathname;
    if (pathname?.length > 0 && !pathname?.includes("admin-users")) {
      localStorage.removeItem("offset");
    }
    dispatch(userProfileAction.setDefaultSelectedTab(1));
  };

  useEffect(() => {
    setOpen(true);
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      lastProfileTokenRef.current = null;
      profileRequestTokenRef.current = null;
      return;
    }

    const tokenChanged = lastProfileTokenRef.current !== authToken;
    if (tokenChanged) {
      lastProfileTokenRef.current = authToken;
      profileRequestTokenRef.current = null;
      dispatch(userProfileAction.clearUserProfileData());
    }

    if (userProfileDetails?.success && !tokenChanged) {
      return;
    }

    if (profileRequestTokenRef.current === authToken) {
      return;
    }

    profileRequestTokenRef.current = authToken;
    dispatch(
      userProfileAction.userProfileData({
        url: "profile",
        data: {},
        token: true,
        silent: true,
      }),
    );
  }, [dispatch, userProfileDetails?.success]);

  useEffect(() => {
    if (window.innerWidth < 575) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (userProfileDetails?.success) {
      rememberAdminPartnerScope(userProfileDetails.data);
    }
    if (
      userProfileDetails?.success &&
      userProfileDetails?.data?.admin_partner_scope !== "car" &&
      userProfileDetails?.data?.requires_admin_unlock_pin_setup
    ) {
      setShowAdminPinSetup(true);
    } else if (userProfileDetails?.data?.has_admin_unlock_pin) {
      setShowAdminPinSetup(false);
    }
  }, [
    userProfileDetails?.success,
    userProfileDetails?.data?.requires_admin_unlock_pin_setup,
    userProfileDetails?.data?.has_admin_unlock_pin,
  ]);

  useEffect(() => {
    if (getObjLength(userProfileDetails) && userProfileDetails?.success) {
      if (userProfileDetails?.data?.status === "inactive") {
        localStorage.clear();
        localStorage.setItem("preference", true);
        redirectToAppPath("login");
      }
    }
  }, [userProfileDetails]);

  const userPreference = (access) => {
    localStorage.setItem("preference", access);
    setOpenAlert(false);
  };
  let superAdmin =
    decryptPayload(localStorage.getItem("roles"))?.includes(ROLES.admin) ||
    danEmail === userProfileDetails?.data?.email ||
    thyEpEmail === userProfileDetails?.data?.email;
  const ucc_Allowed_Admin = decryptPayload(
    localStorage.getItem("ucc_Allowed_Admin"),
  );
  let access =
    (decryptPayload(localStorage.getItem("roles"))?.includes(ROLES.admin) ||
      decryptPayload(localStorage.getItem("roles"))?.includes(
        ROLES.subAdmin,
      )) &&
    !ucc_Allowed_Admin;
  const affiliate_Admin = decryptPayload(
    localStorage.getItem("roles"),
  )?.includes(ROLES.AffiliateManager);
  const { canNav } = useAdminPermissions();
  const source = userProfileDetails?.data?.source;
  // Filter out the prefix from ALLOWED_PREFIXES and allow to enter if found, otherwise redirect
  const allowedPrefixObj = findAllowedPrefixData(source);
  const roleList = decryptPayload(localStorage.getItem("roles"));
  const isCountyRecorder = isCountyRecorderRole(roleList);
  let county_admin =
    roleList?.includes(ROLES.countyRecorder) || affiliate_Admin;

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          open={open}
          className={open ? "open" : "close"}
        >
          <Toolbar className="toolbar">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setOpen(!open)}
              edge="start"
            >
              <img
                src={MenuIconImg}
                alt="Menu Icon"
                className="d-none d-md-block"
              />
              <MenuIcon color="action" className="d-block d-md-none" />
            </IconButton>
            <Link
              className="text-white text-center text-decoration-none"
              to={`${
                getObjLength(allowedPrefixObj)
                  ? `/${allowedPrefixObj?.prefix}/`
                  : "/"
              }`}
            >
              <img
                src={SidebarLogo}
                alt="Logo"
                className="d-block d-md-none"
                style={{ height: "30px", width: "84px" }}
              />
            </Link>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                ml: "auto",
              }}
            >
              {/* <NotificationMenu /> */}
              <AdminAccountMenu />
              <div className="px-1 px-md-3">
                <CallUsNow
                  type="help"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                />
              </div>
            </Box>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="permanent"
          open={open}
          className={
            open
              ? window.innerWidth < 575
                ? "open position-absolute"
                : "open"
              : window.innerWidth < 575
                ? "close position-absolute"
                : "close"
          }
        >
          <DrawerHeader
            sx={{ minHeight: "24px" }}
            className={`${
              !open ? "header_height" : ""
            } d-none d-md-flex drawer_header`}
          >
            <div
              className="sidebarLogoWrapper d-none d-md-block"
              style={{ width: open ? "80%" : "100%", cursor: "pointer" }}
            >
              <Link
                className="text-white text-center text-decoration-none"
                to={`${
                  getObjLength(allowedPrefixObj)
                    ? `/${allowedPrefixObj?.prefix}/`
                    : "/"
                }`}
              >
                <Box sx={{ display: open ? "visible" : "none" }}>
                  <img src={SidebarLogo} alt="Sidebar Logo" />
                </Box>
                <Box sx={{ display: open ? "none" : "visible" }}>
                  <img
                    src={LogoIcon}
                    alt="Logo Icon"
                    style={{ maxHeight: "24px" }}
                  />
                </Box>
              </Link>
            </div>
          </DrawerHeader>
          <List className="menuItemsList admin_sidebar d-flex flex-column">
            {isCountyRecorder ? (
              <CountyRecorderMenu
                open={open}
                onNavigate={handleModalClosing}
                isCarPartner={carPartnerFromProfile(userProfileDetails)}
              />
            ) : (
            <>
            {access && !county_admin && (
              <ListItem disablePadding>
                <Link
                  className={`text-white text-center text-decoration-none ${
                    window.location.pathname
                      .split("/")
                      .includes("admin-dashboard")
                      ? "active"
                      : ""
                  }`}
                  onClick={handleModalClosing}
                  to="/admin-dashboard"
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      width: 250,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <PropertiesIconSVG />
                    </ListItemIcon>
                    <ListItemText
                      className={`menu-item`}
                      primary={"Dashboard"}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            )}
            <ListItem disablePadding>
              <Link
                className={`text-white text-center text-decoration-none ${
                  window.location.pathname.includes("/admin/users") ||
                  window.location.pathname.includes("admin-users")
                    ? "active"
                    : ""
                }`}
                onClick={handleModalClosing}
                to="/admin/users"
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    width: 250,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 1 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={ProvidersIcon}
                      alt="ProvidersIcon"
                      className="icon_width"
                    />
                  </ListItemIcon>
                  <ListItemText
                    className={`menu-item`}
                    primary={"Users"}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </Link>
            </ListItem>

            {access && !county_admin && (
              <ListItem disablePadding>
                <Link
                  className={`text-white text-center text-decoration-none ${
                    window.location.pathname.includes("/admin/risk-assessments")
                      ? "active"
                      : ""
                  }`}
                  onClick={handleModalClosing}
                  to="/admin/risk-assessments"
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      width: 250,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <PollOutlinedIcon
                        className="icon_width"
                        sx={{ color: "#b1c7b9" }}
                        aria-label="Risk Assessments"
                      />
                    </ListItemIcon>
                    <ListItemText
                      className="menu-item"
                      primary="Risk Assessments"
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            )}

            {access && !county_admin && (
              <ListItem disablePadding>
                <Link
                  className={`text-white text-center text-decoration-none ${
                    window.location.pathname.includes("/admin/sp-fulfillment")
                      ? "active"
                      : ""
                  }`}
                  onClick={handleModalClosing}
                  to="/admin/sp-fulfillment"
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      width: 250,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <MapOutlinedIcon
                        className="icon_width"
                        sx={{ color: "#b1c7b9" }}
                        aria-label="SP Territory Fulfillment"
                      />
                    </ListItemIcon>
                    <ListItemText
                      className="menu-item"
                      primary="SP Fulfillment"
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            )}

            {access && !county_admin && (
              <ListItem disablePadding>
                <Link
                  className={`text-white text-center text-decoration-none ${
                    window.location.pathname.includes("/admin/reports")
                      ? "active"
                      : ""
                  }`}
                  onClick={handleModalClosing}
                  to="/admin/reports?view=property"
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      width: 250,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <PollOutlinedIcon
                        className="icon_width"
                        sx={{ color: "#b1c7b9" }}
                        aria-label="Reports"
                      />
                    </ListItemIcon>
                    <ListItemText
                      className="menu-item"
                      primary="Reports"
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            )}

            {access && canNav("partners") && (
              <ListItem disablePadding>
                <Link
                  className={`text-white text-center text-decoration-none ${
                    window.location.pathname.includes("/admin/partners")
                      ? "active"
                      : ""
                  }`}
                  onClick={handleModalClosing}
                  to="/admin/partners"
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      width: 250,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={ProfessionalsIcon}
                        alt="PartnersIcon"
                        className="icon_width"
                      />
                    </ListItemIcon>
                    <ListItemText
                      className="menu-item"
                      primary={"Organizations"}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            )}

            {canNav("marketing") && (
              <ListItem disablePadding>
                <Link
                  className={`text-white text-center text-decoration-none ${
                    window.location.pathname.includes("/admin/marketing")
                      ? "active"
                      : ""
                  }`}
                  onClick={handleModalClosing}
                  to="/admin/marketing"
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      width: 250,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={SendInviteIcon}
                        alt="MarketingIcon"
                        className="icon_width"
                      />
                    </ListItemIcon>
                    <ListItemText
                      className="menu-item"
                      primary={"Marketing"}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            )}

            {canNav("settings") && (
              <ListItem disablePadding>
                <Link
                  className={`text-white text-center text-decoration-none ${
                    window.location.pathname.includes("/admin/settings")
                      ? "active"
                      : ""
                  }`}
                  onClick={handleModalClosing}
                  to="/admin/settings"
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      width: 250,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={LogsIcon}
                        alt="SettingsIcon"
                        className="icon_width"
                      />
                    </ListItemIcon>
                    <ListItemText
                      className="menu-item"
                      primary={"Settings"}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            )}

            {canNav("internal") && (
              <ListItem disablePadding>
                <Link
                  className={`text-white text-center text-decoration-none ${
                    window.location.pathname.includes("/admin/internal")
                      ? "active"
                      : ""
                  }`}
                  onClick={handleModalClosing}
                  to="/admin/internal"
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      width: 250,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={PromocodeIcon}
                        alt="InternalAdminsIcon"
                        className="icon_width"
                      />
                    </ListItemIcon>
                    <ListItemText
                      className="menu-item"
                      primary={"Internal"}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            )}

            {false && (
              <>
                <ListItem disablePadding>
                  <Link
                    className={`text-white text-center text-decoration-none ${
                      window.location.pathname
                        .split("/")
                        .includes("admin-users")
                        ? "active"
                        : ""
                    }`}
                    onClick={handleModalClosing}
                    to="/admin-users"
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        width: 250,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 1 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={ProvidersIcon}
                          alt="ProvidersIcon"
                          className="icon_width"
                        />
                      </ListItemIcon>
                      <ListItemText
                        className={`menu-item`}
                        primary={"Users"}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </Link>
                </ListItem>

                <ListItem disablePadding>
                  <Link
                    className={`text-white text-center text-decoration-none ${
                      window.location.pathname
                        .split("/")
                        .includes("admin-properties")
                        ? "active"
                        : ""
                    }`}
                    onClick={() => {
                      handleModalClosing();
                      dispatch(propertyUsersAction.setSelectedProperty({}));
                    }}
                    to="/admin-properties"
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        width: 250,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 1 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={PropertiesIcon}
                          alt="PropertiesIcon"
                          className="icon_width"
                        />
                      </ListItemIcon>
                      <ListItemText
                        className={`menu-item`}
                        primary={"Properties"}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </Link>
                </ListItem>
                {(access || county_admin) && (
                  <ListItem disablePadding>
                    <Link
                      className={`text-white text-center text-decoration-none ${
                        window.location.pathname
                          .split("/")
                          .includes("admin-property-monitoring")
                          ? "active"
                          : ""
                      }`}
                      onClick={handleModalClosing}
                      to="/admin-property-monitoring"
                    >
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          width: 250,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 1 : "auto",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={PropertiesMonitoringIcon}
                            alt="PropertiesMonitoringIcon"
                          />
                        </ListItemIcon>
                        <ListItemText
                          className="menu-item"
                          primary={"Property Monitoring"}
                          sx={{ opacity: open ? 1 : 0 }}
                        />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                )}

                {access && (
                  <ListItem disablePadding>
                    <Link
                      className={`text-white text-center text-decoration-none ${
                        window.location.pathname
                          .split("/")
                          .includes("add-new-users")
                          ? "active"
                          : ""
                      }`}
                      onClick={() => {
                        handleModalClosing();
                        dispatch(userSigupAction.clearData());
                        dispatch(userSigupAction.userAddedByAdmin(true));
                        dispatch(
                          propertiesAction.setIsBypassOptionAvailable(true),
                        );
                      }}
                      to="/add-new-users"
                    >
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          width: 250,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 1 : "auto",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={AddNewUserIcon}
                            alt="AddNewUserIcon"
                            className="icon_width"
                          />
                        </ListItemIcon>
                        <ListItemText
                          className="menu-item"
                          primary={"Add new user"}
                          sx={{ opacity: open ? 1 : 0 }}
                        />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                )}
                {decryptPayload(localStorage.getItem("roles"))?.includes(
                  ROLES.admin,
                ) && (
                  <ListItem disablePadding>
                    <Link
                      className={`text-white text-center text-decoration-none ${
                        window.location.pathname
                          .split("/")
                          .includes("admin-affiliate-company")
                          ? "active"
                          : ""
                      }`}
                      onClick={handleModalClosing}
                      to="/admin-affiliate-company"
                    >
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          width: 250,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 1 : "auto",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={TitleCompanyIcon}
                            alt="AffiliateCompanyIcon"
                          />
                        </ListItemIcon>
                        <ListItemText
                          className="menu-item"
                          primary={"Affiliate Companies"}
                          sx={{ opacity: open ? 1 : 0 }}
                        />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                )}

                {(access || ucc_Allowed_Admin) && (
                  <ListItem disablePadding>
                    <Link
                      className={`text-white text-center text-decoration-none ${
                        window.location.pathname
                          .split("/")
                          .includes("admin-ucc-status")
                          ? "active"
                          : ""
                      }`}
                      onClick={handleModalClosing}
                      to="/admin-ucc-status"
                    >
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          width: 250,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 1 : "auto",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={RequestDocumentsIcon}
                            alt="UCCRequestIcon"
                          />
                        </ListItemIcon>
                        <ListItemText
                          className="menu-item"
                          primary={"UCC Status"}
                          sx={{ opacity: open ? 1 : 0 }}
                        />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                )}

                {access && (
                  <>
                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("admin-professionals")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/admin-professionals"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={ProfessionalsIcon}
                              alt="ProfessionalsIcon"
                              className="icon_width"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Professionals"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("add-leads")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/add-leads"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            {/* <LockIconSVG /> */}
                            {/* <PersonAddAltIcon /> */}
                            <img
                              src={SendInviteIcon}
                              alt="SendInviteIcon"
                              className="icon_width"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Send Invite"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("admin-survey")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/admin-survey"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <PollOutlinedIcon
                              className="icon_width"
                              sx={{ color: "#b1c7b9" }}
                              aria-label="Survey"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Survey"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("promocodes")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/promocodes"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            {/* <CodeOffOutlinedIcon /> */}
                            {/* <SubtitlesOutlinedIcon /> */}
                            <img
                              src={PromocodeIcon}
                              alt="PromocodeIcon"
                              className="icon_width"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Promo Codes"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("service-providers")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/service-providers"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={ServiceProvidersIcon}
                              alt="ServiceProvidersIcon"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Service Providers"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname.split("/").includes("admins")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/admins"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={PromocodeIcon}
                              alt="AdminsIcon"
                              className="icon_width"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Admins"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("admin-providers")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/admin-providers"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={ProvidersIcon}
                              alt="ProvidersIcon"
                              className="icon_width"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Providers"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("admin-county")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/admin-county"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={CountyIcon}
                              alt="CountyIcon"
                              className="icon_width"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"County"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("customer-service-queue")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/customer-service-queue"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={RequestDocumentsIcon}
                              alt="CustomerServiceQueueIcon"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Customer Service Queue"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("request-document-list")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/request-document-list"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={RequestDocumentsIcon}
                              alt="RequestDocumentsIcon"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Request Documents"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("admin-recording-fees")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/admin-recording-fees"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={RecordingFeesIcon}
                              alt="RecordingFeesIcon"
                              className="icon_width"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Recording Fees"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    {superAdmin && (
                      <ListItem disablePadding>
                        <Link
                          className={`text-white text-center text-decoration-none ${
                            window.location.pathname
                              .split("/")
                              .includes("admin-export-csv")
                              ? "active"
                              : ""
                          }`}
                          onClick={handleModalClosing}
                          to="/admin-export-csv"
                        >
                          <ListItemButton
                            sx={{
                              minHeight: 48,
                              width: 250,
                              justifyContent: open ? "initial" : "center",
                              px: 2.5,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: open ? 1 : "auto",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={ExportIcon}
                                alt="ExportIcon"
                                className="icon_width"
                              />
                            </ListItemIcon>
                            <ListItemText
                              className="menu-item"
                              primary={"Export Payments"}
                              sx={{ opacity: open ? 1 : 0 }}
                            />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}

                    <ListItem disablePadding>
                      <Link
                        className={`text-white text-center text-decoration-none ${
                          window.location.pathname
                            .split("/")
                            .includes("admin-logs")
                            ? "active"
                            : ""
                        }`}
                        onClick={handleModalClosing}
                        to="/admin-logs"
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            width: 250,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 1 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={LogsIcon}
                              alt="LogsIcon"
                              className="icon_width"
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="menu-item"
                            primary={"Logs"}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>

                    <ListItem disablePadding>
                      <p>
                        <br />
                      </p>
                    </ListItem>
                  </>
                )}
              </>
            )}

            {access && (
              <ListItem disablePadding>
                <Link
                  className={`text-white text-center text-decoration-none ${
                    window.location.pathname
                      .split("/")
                      .includes("customer-service-queue")
                      ? "active"
                      : ""
                  }`}
                  onClick={handleModalClosing}
                  to="/customer-service-queue"
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      width: 250,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={RequestDocumentsIcon}
                        alt="CustomerServiceQueueIcon"
                        className="icon_width"
                      />
                    </ListItemIcon>
                    <ListItemText
                      className="menu-item"
                      primary={"Customer Service Queue"}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            )}
            {access && (
              <ListItem disablePadding>
                <Link
                  className={`text-white text-center text-decoration-none ${
                    window.location.pathname
                      .split("/")
                      .includes("request-document-list")
                      ? "active"
                      : ""
                  }`}
                  onClick={handleModalClosing}
                  to="/request-document-list"
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      width: 250,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={RequestDocumentsIcon}
                        alt="RequestDocumentsIcon"
                        className="icon_width"
                      />
                    </ListItemIcon>
                    <ListItemText
                      className="menu-item"
                      primary={"Requested Documents"}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            )}
            </>
            )}
          </List>
          {/* <Stack
            direction={open ? "row" : "column"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            className="text-center border-top pt-2"
            sx={{}}
          >
            <a
              href={INSTAGRAM_LINK}
              target="_blank"
              rel="noreferrer"
              className="px-2 pt-2"
            >
              <img
                src={Instagram_Icon}
                alt="Facebook"
                style={{ height: "25px" }}
              />
            </a>

            <a
              href={FACEBOOK_LINK}
              target="_blank"
              rel="noreferrer"
              className="px-2 pt-2"
            >
              <img
                src={Facebook_Icon}
                alt="Facebook"
                style={{ height: "25px" }}
              />
            </a>
          </Stack> */}
          {/* <Box
            className="text-center p-0 pb-3"
            p={3}
            sx={{ display: open ? "visible" : "none" }}
          >
            <CallUsNow
              type="text"
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            />
          </Box>
          <Box
            className="text-center p-0 py-2"
            sx={{ display: open ? "none" : "block", py: 3 }}
          >
            <div className="call cursor-pointer">
              <CallUsNow
                type="icon"
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              />
            </div>
          </Box> */}
        </Drawer>
        <Box
          component="main"
          className="main admin-main-shell mt-5 mt-md-0 pb-0 pb-md-0"
          sx={{ flexGrow: 1, overflowX: "hidden" }}
        >
          <DrawerHeader sx={{ minHeight: "64px !important" }} />
          {userProfileDetails?.success ? (
            pageContent
          ) : localStorage.getItem("authToken") &&
            (!getObjLength(userProfileDetails) || profileLoader) ? (
            <Box className="text-center py-5">
              <Loader open />
            </Box>
          ) : (
            <Box className="text-center">
              <span
                className="text-danger text-capitalize"
                // onClick={clearUserData}
              >
                {userProfileDetails?.data?.message ||
                userProfileDetails?.message ? (
                  <>
                    {`${
                      userProfileDetails?.data?.message ||
                      userProfileDetails?.message
                    }`}
                    <Link
                      to={"/login"}
                      className="bold text-decoration-none"
                      onClick={clearUserData}
                    >
                      &nbsp;Click here to Login again.
                    </Link>
                  </>
                ) : (
                  <>
                    Unable to load your admin profile.
                    <Link
                      to={"/login"}
                      className="bold text-decoration-none"
                      onClick={clearUserData}
                    >
                      &nbsp;Click here to Login again.
                    </Link>
                  </>
                )}
              </span>
            </Box>
          )}
          {/* {comp} */}
        </Box>
        <CookieConsentBanner
          isVisible={openAlert}
          onAccept={() => userPreference(true)}
          onDecline={() => userPreference(false)}
          userPreferences={
            localStorage.getItem("preference") === null ||
            localStorage.getItem("preference").length === 0
          }
          isTab={isTab}
          code="web"
        />
      </Box>

      {open && window.innerWidth < 575
        ? ReactDOM.createPortal(
            <>
              <Backdrop
                sx={{ zIndex: 995 }}
                open={open}
                onClick={() => {
                  setOpen((prevState) => !prevState);
                }}
              />
            </>,
            document.body,
          )
        : null}

      <AdminUnlockPinSetupModal
        open={showAdminPinSetup}
        userEmail={userProfileDetails?.data?.email}
        onComplete={() => setShowAdminPinSetup(false)}
      />
    </>
  );
}
